/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package repository

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import models.*
import studio.PaymentFrequency

/**
 * API DTOs for syncing with server
 */
@Serializable
data class CreateGoalApiRequest(
    val id: String,
    val user_id: String,
    val name: String,
    val description: String?,
    val target_amount: Double,
    val current_amount: Double,
    val deadline: String,
    val category: String,
    val priority: String,
    val created_date: String,
    val is_active: Boolean,
    val icon_emoji: String?,
    val account_type: String? = null,
    val debt_interest_rate: Double? = null,
    val debt_remaining_balance: Double? = null,
    val debt_remaining_months: Int? = null,
    val debt_payment_frequency: String? = null,
    val debt_monthly_equivalent_payment: Double? = null,
)

@Serializable
data class UpdateGoalApiRequest(
    val id: String,
    val user_id: String,
    val name: String,
    val description: String?,
    val target_amount: Double,
    val deadline: String,
    val category: String,
    val priority: String,
    val icon_emoji: String?,
    val account_type: String? = null,
    val debt_interest_rate: Double? = null,
    val debt_remaining_balance: Double? = null,
    val debt_remaining_months: Int? = null,
    val debt_payment_frequency: String? = null,
    val debt_monthly_equivalent_payment: Double? = null,
)

@Serializable
data class UpdateProgressApiRequest(
    val goal_id: String,
    val current_amount: Double
)

@Serializable
data class GoalApiResponse(
    val id: String,
    val name: String,
    val description: String?,
    val target_amount: Double,
    val current_amount: Double,
    val deadline: String,
    val category: String,
    val priority: String,
    val created_date: String,
    val is_active: Boolean,
    val icon_emoji: String?,
    // Wiz 3R AI fields — server includes these in GoalResponse. Without
    // them here the kotlinx.serialization deserializer silently drops the
    // values, which is what makes the local DB cache stale after a roundtrip.
    val account_type: String? = null,
    val debt_interest_rate: Double? = null,
    val debt_remaining_balance: Double? = null,
    val debt_remaining_months: Int? = null,
    val debt_payment_frequency: String? = null,
    val debt_monthly_equivalent_payment: Double? = null,
)

@Serializable
data class GoalsListApiResponse(
    val goals: List<GoalApiResponse>
)

/**
 * Sync service for keeping goals synchronized between local database and server
 */
class SavingsGoalsSyncService(
    private val baseUrl: String = "http://localhost:3000",
    private val httpClient: HttpClient = createDefaultHttpClient(),
    private val getAuthToken: suspend () -> String? = { null }
) {

    companion object {
        fun createDefaultHttpClient() = HttpClient {
            install(ContentNegotiation) {
                json(Json {
                    ignoreUnknownKeys = true
                    encodeDefaults = true
                    prettyPrint = true
                })
            }
        }
    }

    private suspend fun HttpRequestBuilder.addAuthHeader() {
        getAuthToken()?.let { token ->
            header("Authorization", "Bearer $token")
        }
    }

    /**
     * Sync a goal to the server (create or update)
     */
    suspend fun syncGoalToServer(goal: SavingsGoal, userId: String): Result<Unit> {
        return try {
            val authToken = getAuthToken()
            println("🔑 SyncService: Auth token ${if (authToken != null) "available (${authToken.take(10)}...)" else "NOT available"}")
            println("📡 SyncService: Syncing goal '${goal.name}' (id=${goal.id}) for userId=$userId to $baseUrl/api/goals")

            // Try to create first
            val createRequest = CreateGoalApiRequest(
                id = goal.id,
                user_id = userId,
                name = goal.name,
                description = goal.description,
                target_amount = goal.targetAmount,
                current_amount = goal.currentAmount,
                deadline = goal.deadline.toString(),
                category = goal.category.name,
                priority = goal.priority.name,
                created_date = goal.createdDate.toString(),
                is_active = goal.isActive,
                icon_emoji = goal.iconEmoji,
                account_type = goal.accountType?.name,
                debt_interest_rate = goal.debtInterestRate,
                debt_remaining_balance = goal.debtRemainingBalance,
                debt_remaining_months = goal.debtRemainingMonths,
                debt_payment_frequency = goal.debtPaymentFrequency?.name,
                debt_monthly_equivalent_payment = goal.debtMonthlyEquivalentPayment,
            )

            val createResponse = httpClient.post("$baseUrl/api/goals") {
                contentType(ContentType.Application.Json)
                addAuthHeader()
                setBody(createRequest)
            }

            println("📡 SyncService: Server response status=${createResponse.status}")

            if (createResponse.status.isSuccess()) {
                println("✅ SyncService: Goal created on server successfully")
                Result.success(Unit)
            } else if (createResponse.status == HttpStatusCode.Conflict ||
                       createResponse.status == HttpStatusCode.BadRequest) {
                // Goal might already exist, try updating
                println("⚠️ SyncService: Goal may exist, trying update instead")
                updateGoalOnServer(goal, userId)
            } else {
                val errorBody = createResponse.bodyAsText()
                println("❌ SyncService: Server returned ${createResponse.status}: $errorBody")
                Result.failure(Exception("Server returned ${createResponse.status}: $errorBody"))
            }
        } catch (e: Exception) {
            println("❌ SyncService: Exception during sync: ${e.message}")
            e.printStackTrace()
            Result.failure(e)
        }
    }

    /**
     * Update an existing goal on the server
     */
    suspend fun updateGoalOnServer(goal: SavingsGoal, userId: String): Result<Unit> {
        return try {
            val updateRequest = UpdateGoalApiRequest(
                id = goal.id,
                user_id = userId,
                name = goal.name,
                description = goal.description,
                target_amount = goal.targetAmount,
                deadline = goal.deadline.toString(),
                category = goal.category.name,
                priority = goal.priority.name,
                icon_emoji = goal.iconEmoji,
                account_type = goal.accountType?.name,
                debt_interest_rate = goal.debtInterestRate,
                debt_remaining_balance = goal.debtRemainingBalance,
                debt_remaining_months = goal.debtRemainingMonths,
                debt_payment_frequency = goal.debtPaymentFrequency?.name,
                debt_monthly_equivalent_payment = goal.debtMonthlyEquivalentPayment,
            )

            val response = httpClient.put("$baseUrl/api/goals/${goal.id}") {
                contentType(ContentType.Application.Json)
                addAuthHeader()
                setBody(updateRequest)
            }

            if (response.status.isSuccess()) {
                Result.success(Unit)
            } else {
                Result.failure(Exception("Server returned ${response.status}: ${response.bodyAsText()}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Fetch all active goals from server for a user
     */
    suspend fun fetchActiveGoalsFromServer(userId: String): Result<List<SavingsGoal>> {
        return try {
            val response = httpClient.get("$baseUrl/api/goals/active/$userId") {
                addAuthHeader()
            }

            if (response.status.isSuccess()) {
                val goalsListResponse: GoalsListApiResponse = response.body()
                val goals = goalsListResponse.goals.map { it.toSavingsGoal() }
                Result.success(goals)
            } else {
                Result.failure(Exception("Server returned ${response.status}: ${response.bodyAsText()}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Fetch all goals (including inactive) from server for a user
     */
    suspend fun fetchAllGoalsFromServer(userId: String): Result<List<SavingsGoal>> {
        return try {
            val authToken = getAuthToken()
            println("🔑 SyncService.fetchAllGoals: Auth token ${if (authToken != null) "available (${authToken.take(10)}...)" else "NOT available"}")
            println("📡 SyncService.fetchAllGoals: Fetching goals for userId=$userId from $baseUrl/api/goals/all/$userId")

            val response = httpClient.get("$baseUrl/api/goals/all/$userId") {
                addAuthHeader()
            }

            println("📡 SyncService.fetchAllGoals: Server response status=${response.status}")

            if (response.status.isSuccess()) {
                val goalsListResponse: GoalsListApiResponse = response.body()
                val goals = goalsListResponse.goals.map { it.toSavingsGoal() }
                println("✅ SyncService.fetchAllGoals: Successfully fetched ${goals.size} goals")
                Result.success(goals)
            } else {
                val errorBody = response.bodyAsText()
                println("❌ SyncService.fetchAllGoals: Server returned ${response.status}: $errorBody")
                Result.failure(Exception("Server returned ${response.status}: $errorBody"))
            }
        } catch (e: Exception) {
            println("❌ SyncService.fetchAllGoals: Exception: ${e.message}")
            e.printStackTrace()
            Result.failure(e)
        }
    }

    /**
     * Update goal progress on the server
     */
    suspend fun updateProgressOnServer(goalId: String, currentAmount: Double): Result<Unit> {
        return try {
            val request = UpdateProgressApiRequest(
                goal_id = goalId,
                current_amount = currentAmount
            )

            val response = httpClient.patch("$baseUrl/api/goals/$goalId/progress") {
                contentType(ContentType.Application.Json)
                addAuthHeader()
                setBody(request)
            }

            if (response.status.isSuccess()) {
                Result.success(Unit)
            } else {
                Result.failure(Exception("Server returned ${response.status}: ${response.bodyAsText()}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Delete a goal on the server
     */
    suspend fun deleteGoalOnServer(goalId: String, userId: String): Result<Unit> {
        return try {
            val response = httpClient.delete("$baseUrl/api/goals/$goalId/$userId") {
                addAuthHeader()
            }

            if (response.status.isSuccess()) {
                Result.success(Unit)
            } else {
                Result.failure(Exception("Server returned ${response.status}: ${response.bodyAsText()}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}

/**
 * Convert API response to domain model
 */
private fun GoalApiResponse.toSavingsGoal() = SavingsGoal(
    id = id,
    name = name,
    description = description ?: "",
    targetAmount = target_amount,
    currentAmount = current_amount,
    deadline = LocalDate.parse(deadline),
    category = GoalCategory.valueOf(category),
    priority = GoalPriority.valueOf(priority),
    createdDate = Instant.parse(created_date),
    isActive = is_active,
    iconEmoji = icon_emoji ?: "💰",
    deadlineStr = deadline,  // Store the string value for Kotlin/JS
    accountType = account_type?.let { runCatching { GoalAccountType.valueOf(it) }.getOrNull() },
    debtInterestRate = debt_interest_rate,
    debtRemainingBalance = debt_remaining_balance,
    debtRemainingMonths = debt_remaining_months,
    debtPaymentFrequency = debt_payment_frequency?.let { runCatching { PaymentFrequency.valueOf(it) }.getOrNull() },
    debtMonthlyEquivalentPayment = debt_monthly_equivalent_payment,
)
